import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    Typography,
    CircularProgress,
    useTheme,
    TextField,
} from "@mui/material";
import { Close, SearchOutlined } from "@mui/icons-material";
import { SavePowerStationUserReactQuery, UsersAssociationReactQuery } from "../PowerStationReactQuery";
import { LoadingButton } from "@mui/lab";
import {useAppSelector} from "../../../../Config/Hooks";
import {hasPermission} from "../../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../../Config/Constants";

interface User {
    id: number;
    name: string;
    email: string;
    hasPermission: boolean;
}
interface UserAssociationPanelProps {
    open: boolean;
    onClose: () => void;
    powerStationId: number;
    powerStationName: string;
}

export default function UserAssociationPanel({
    open,
    onClose,
    powerStationId,
    powerStationName,
}: UserAssociationPanelProps) {

    const theme = useTheme();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_POWER_STATION_USER
    ]);
    const isAuthorizedToDelete = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.DELETE_POWER_STATION_USER
    ]);

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [filterType, setFilterType] = useState<"all" | "withAccess" | "withoutAccess">("all");
    const mutationSave = SavePowerStationUserReactQuery();

    const { data, isLoading, error } = UsersAssociationReactQuery(powerStationId, open);
    const [users, setUsers] = useState<User[]>([]);
    const [openSearch, setOpenSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!open) return;

        setFilterType("all");
        setUsers([]); 
        setSelectedUsers([]);
        setSearchTerm("");
        setOpenSearch(false);
    }, [open]);

    useEffect(() => {
        if (!open || !data?.data) return;

        setUsers(data.data);
        setSelectedUsers(data.data.filter((u: User) => u.hasPermission).map((u: User) => u.id));
    }, [data?.data, open]);


    const filteredUsers = useMemo(() => {
        if (filterType === "all") return users;
        if (filterType === "withAccess") return users.filter((u) => u.hasPermission);
        return users.filter((u) => !u.hasPermission);
    }, [users, filterType]);

    const handleToggleUser = (userId: number) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

const handleSelectAll = () => {
    const filteredIds = filteredUsers.map((u) => u.id);
    const allFilteredSelected =
        filteredIds.length > 0 && filteredIds.every((id) => selectedUsers.includes(id));

    if (allFilteredSelected) {
        setSelectedUsers((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
        setSelectedUsers((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
};

    const isAllSelected =
        filteredUsers.length > 0 &&
        filteredUsers.every((u) => selectedUsers.includes(u.id));

    useEffect(() => {
        const allUsers = data?.data || [];
        if (searchTerm.length > 0) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            setUsers(
                allUsers.filter(
                    (u) =>
                        u.name.toLowerCase().includes(lowerSearchTerm) ||
                        u.email.toLowerCase().includes(lowerSearchTerm)
                )
            );
        } else {
            setUsers(allUsers);
        }
    }, [searchTerm, data?.data]);


    const savePowerStationUsers = (userIds: number[]) => {
        const powerStationSave = powerStationId;
        const allUsers = data?.data || [];
        const userSaveList = allUsers.filter((u) => userIds.includes(u.id))
        .map((u) => ({ id: null, powerStation: {id: powerStationSave}, user: { id: u.id, name: u.name, email: u.email } }));
        
        mutationSave.mutate(userSaveList, {
            onSuccess: (data) => {
                if(data?.data) {
                    onClose();
                }
            }
        })
    }

    if (!open) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                right: 0,
                top: 0,
                height: "100vh",
                width: { xs: "80%", sm: 480 },
                backgroundColor: theme.palette.background.paper,
                boxShadow: "-2px 0px 8px rgba(0,0,0,0.15)",
                display: "flex",
                flexDirection: "column",
                zIndex: 1300,
                borderRadius: "4px 0 0 4px",
                animation: "slideIn 0.3s ease-in-out",
                "@keyframes slideIn": {
                    from: { transform: "translateX(100%)" },
                    to: { transform: "translateX(0)" },
                },
            }}
        >
            {/* Header Section */}
            <Box sx={{ p: 3, pb: 0 }}>
                {/* Title and Close Button */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: 24,
                        }}
                    >
                        Associar usuários
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            p: "5px",
                            borderRadius: "100px",
                            color:theme.palette.primary.main,
                            "&:hover": {
                                backgroundColor: "rgba(45, 93, 231, 0.1)",
                            },
                        }}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                {/* Divider */}
                <Divider
                    sx={{
                        backgroundColor: "#FAFBFF",
                        borderColor: "rgba(0,0,0,0.12)",
                        mb: 2,
                    }}
                />

                {/* Power Station Info */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            flex: 1,
                            letterSpacing: "0.17px",
                        }}
                    >
                        Usina
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            flex: 1,
                            textAlign: "right",
                        }}
                    >
                        {powerStationName}
                    </Typography>
                </Box>

                <Divider sx={{backgroundColor: "#FAFBFF", borderColor: "rgba(0,0,0,0.12)", mb: 2 }} />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Chip
                            label="Com acesso"
                            variant={filterType === "withAccess" ? "filled" : "outlined"}
                            onClick={() => setFilterType(filterType === "withAccess" ? "all" : "withAccess")}
                            sx={{
                                height: 28,
                                fontSize: 13,
                                borderRadius: "100px",
                                backgroundColor:
                                    filterType === "withAccess" ? theme.palette.primary.main : "transparent",
                                color: filterType === "withAccess" ? theme.palette.mode === "light" ? "#FFFFFF" : "rgba(0,0,0,0.87)" : theme.palette.mode === "light" ? "rgba(0,0,0,0.87)" : "#FFFFFF",
                                border:
                                    filterType === "withAccess"
                                        ? "none"
                                        : "1px solid #BDBDBD",
                                "&:hover": {
                                    backgroundColor:
                                        filterType === "withAccess"
                                            ? "#2454C4"
                                            : "rgba(0,0,0,0.04)",
                                },
                            }}
                        />
                        <Chip
                            label="Sem acesso"
                            variant={filterType === "withoutAccess" ? "filled" : "outlined"}
                            onClick={() =>
                                setFilterType(
                                    filterType === "withoutAccess" ? "all" : "withoutAccess"
                                )
                            }
                            sx={{
                                height: 28,
                                fontSize: 13,
                                borderRadius: "100px",
                                backgroundColor:
                                    filterType === "withoutAccess" ? theme.palette.primary.main : "transparent",
                                color:
                                    filterType === "withoutAccess"
                                        ? theme.palette.mode === "light" ? "#FFFFFF" : "rgba(0,0,0,0.87)" : theme.palette.mode === "light" ? "rgba(0,0,0,0.87)" : "#FFFFFF",
                                border:
                                    filterType === "withoutAccess"
                                        ? "none"
                                        : "1px solid #BDBDBD",
                                "&:hover": {
                                    backgroundColor:
                                        filterType === "withoutAccess"
                                            ? "#2454C4"
                                            : "rgba(0,0,0,0.04)",
                                },
                            }}
                        />
                    </Box>

                    {/* Avatar */}
                    <Box
                        onClick={() => setOpenSearch((prev) => !prev)}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "100%",
                            backgroundColor: theme.palette.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            cursor: "pointer",
                        }}
                    >
                        <SearchOutlined
                            sx={{
                                fontSize: 24,
                                color: theme.palette.mode === "light" ? "#FFFFFF" : "#051F36",
                            }}
                        />
                    </Box>
                </Box>
                {openSearch &&
                <Box sx={{ pt: 2}}>
                    <TextField
                        placeholder="Buscar"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                            style: {
                                borderRadius: 4,
                            },
                            endAdornment: (
                                <SearchOutlined
                                    sx={{
                                        fontSize: 20,
                                        color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                                        mr: 1,
                                    }}
                                />
                            ),
                        }}
                        sx={{ width: "100%" }}
                    />
                </Box>

                }
            </Box>

            {/* Users List Section */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    m:2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    backgroundColor: theme.palette.mode === "light" ? "#FAFBFF" : "rgba(81, 81, 81, 1)",
                    color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                    "&::-webkit-scrollbar": {
                        width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#EBF0FF",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#A7A8AC",
                        "&:hover": {
                            backgroundColor: "#888",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                         m: '0 16px',
                    }}
                >
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                            }}
                        >
                            {filterType === "withoutAccess"
                                ? (data?.data?.length ?? 0) - selectedUsers.length
                                : selectedUsers.length}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 400,
                                color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                            }}
                        >
                            de {data?.data?.length ?? 0} usuários
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography
                            onClick={handleSelectAll}
                            sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                                cursor: "pointer",
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            Selecionar todos
                        </Typography>
                        <Checkbox
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            sx={{
                                p: "9px",
                                "& .MuiSvgIcon-root": { fontSize: 20 },
                            }}
                        />
                    </Box>
                </Box>

                <Divider
                    sx={{
                        m: '0 16px',
                        backgroundColor: "#FAFBFF",
                    }}
                />
                {isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>                    
                )}
                {!isLoading && filteredUsers.length === 0 && (
                    <Typography
                        sx={{
                            fontSize: 14,
                            color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                            textAlign: "center",
                            py: 4,
                        }}
                    >
                        Nenhum usuário encontrado
                    </Typography>
                )}

                {!isLoading &&
                    filteredUsers.map((user) => {
                        const hasPermission = users.find(u => u.id === user.id)?.hasPermission;
                        return(
                            <Box
                                key={user.id}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 2,
                                    p: 1.5,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor:theme.palette.mode === "light" ? "#F5F9FF" : "rgb(92, 89, 89)",
                                        borderColor: "#E0E7FF",
                                    },
                                }}
                            >
                                {/* User Info */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                                            lineHeight: "20px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {user.name}
                                    </Typography>
                                    <Typography
                                        title={user.email}
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: theme.palette.mode === "light" ? "#051F36" : "#AFBFF",
                                            lineHeight: "20px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                </Box>

                                {hasPermission && (
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
                                        Associado
                                    </Typography>
                                )}
                                <Checkbox
                                    checked={selectedUsers.includes(user.id)}
                                    onChange={() => handleToggleUser(user.id)}
                                    sx={{
                                        p: "9px",
                                        flexShrink: 0,
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 20,
                                        },
                                    }}
                                />
                            </Box>
                    )})
                }
            </Box>
            <Divider sx={{ backgroundColor: "#FAFBFF", borderColor: "rgba(0,0,0,0.12)", mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, mb: 2, padding: '0 24px' }}>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    loading={isLoading}
                    onClick={() => savePowerStationUsers(selectedUsers)}
                    disabled={(!isAuthorizedToSave || !isAuthorizedToDelete) || (isLoading || selectedUsers.length === 0)}
                >
                    ASSOCIAR USUÁRIOS
                </LoadingButton>
                <LoadingButton variant="outlined" color="secondary" onClick={onClose} loading={isLoading}>
                    CANCELAR
                </LoadingButton>
            </Box>
        </Box>
    );
}
